import Instance from "../../types/Instance";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { UploadCredentials } from "./get-upload-creds";
import * as Sentry from "@sentry/vue";
import { addLog, updateStatus } from "../../logger";
import { stringifyError } from "../../util/stringify-error";
import humanFileSize from "../../humanFileSize.js";

/**
 * Joins an array of paths into a single path, removing any empty paths, and
 * removing extra slashes. Handles including a scheme in the first path
 * fragment.
 */
const joinPaths = (...paths: string[]) =>
  paths
    .filter(Boolean)
    .join("/")
    .replace(/([^:])\/+/g, "$1/");

/**
 * Error thrown when the final upload (litevna POST / S3 PutObject) fails. Carries
 * the HTTP status and response body so they survive up to the Sentry call,
 * instead of being lost behind an opaque JSON parse error.
 */
class UploadError extends Error {
  status?: number;
  body?: string;
  constructor(message: string, info?: { status?: number; body?: string }) {
    super(message);
    this.name = "UploadError";
    this.status = info?.status;
    this.body = info?.body;
  }
}

/**
 * Render an error for the user-facing log list. For an UploadError, append the
 * HTTP status and response body when we have them — these carry the actual
 * server-side reason (e.g. a 413 body) and would otherwise be visible only in
 * Sentry. Falls back to the plain message for everything else.
 */
function describeError(err: unknown): string {
  const base = stringifyError(err);
  if (!(err instanceof UploadError)) return base;
  const parts: string[] = [];
  if (err.status) parts.push(`HTTP ${err.status}`);
  if (err.body) parts.push(err.body);
  return parts.length ? `${base} (${parts.join(" — ")})` : base;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Runs an async fn, retrying on failure up to `retries` times with a simple
 * linear backoff. `onRetry` is invoked before each retry (not after the final
 * failure). Returns the fn's result, or throws the last error once attempts are
 * exhausted.
 */
async function retry<T>(
  fn: (attempt: number) => Promise<T>,
  opts: {
    retries: number;
    baseDelayMs: number;
    onRetry: (err: unknown, attempt: number) => void;
  }
): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= opts.retries + 1; attempt++) {
    try {
      return await fn(attempt);
    } catch (err) {
      lastErr = err;
      if (attempt <= opts.retries) {
        opts.onRetry(err, attempt);
        await delay(opts.baseDelayMs * attempt);
      }
    }
  }
  throw lastErr;
}

// Number of retries after the first attempt (so 2 == 3 attempts total).
const UPLOAD_RETRIES = 2;

// Cloudflare caps request bodies at 100MB. Compressed P10 artifacts at or above
// this size can't be POSTed through the worker in one shot, so they go via the
// native R2 multipart flow instead. Threshold kept under 100MB for header/encoding overhead.
const LITEVNA_DIRECT_POST_LIMIT = 95_000_000;

export default async function uploadInstance(options: {
  instance: Instance;
  uploadCredentials: UploadCredentials;
  fileKey?: string;
  s3Client?: S3Client;
  studyShortid?: string;
  /**
   * Reports upload progress for this instance as a fraction in [0, 1]. Only the
   * litevna path emits real byte-level progress (via XHR); the S3 path has no
   * per-instance progress and never calls this. Fires repeatedly during upload
   * and resets on retry.
   */
  onUploadProgress?: (fraction: number) => void;
}) {
  const {
    instance,
    uploadCredentials,
    fileKey,
    s3Client,
    studyShortid,
    onUploadProgress,
  } = options;

  // The actual upload for a single instance, retried on intermittent failure.
  // create-casedef.ts (case/study/series creation) is upstream of this and is
  // not re-run on retry — only this one PUT/POST is repeated.
  const doUpload = () => {
    if (
      uploadCredentials.storageProvider === "litevna" &&
      uploadCredentials.sessionToken &&
      uploadCredentials.endpoint
    ) {
      return uploadToLitevna();
    } else if (s3Client) {
      return uploadToS3(s3Client);
    }
    throw new Error("No S3 client or upload endpoint provided");
  };

  // Upload part 10 to litevna. endpoint should be the dicomweb root. Small
  // artifacts POST straight through the worker; artifacts at/over Cloudflare's
  // ~100MB request cap can't, so they go via the native R2 multipart flow,
  // chunked under the cap and assembled by the worker on our own domain.
  function uploadToLitevna(): Promise<any> {
    const compressedSize = instance.instanceData.dicomP10Compressed?.size ?? 0;
    if (compressedSize >= LITEVNA_DIRECT_POST_LIMIT) {
      return uploadToLitevnaMultipart();
    }
    return uploadToLitevnaDirect();
  }

  // Sends a request body via XHR so we can report real upload progress via
  // xhr.upload.onprogress — fetch can't observe request-body upload progress.
  // Resolves with the raw response regardless of status; callers interpret it.
  function xhrSend(opts: {
    method: string;
    url: string;
    headers: Record<string, string>;
    body: Blob | null;
    trackProgress?: boolean;
    /**
     * Raw byte progress for this single request. When provided it takes over
     * from the simple trackProgress→onUploadProgress fraction, so the multipart
     * path can aggregate progress across parts itself.
     */
    onRawProgress?: (loaded: number) => void;
  }): Promise<{ status: number; statusText: string; body: string }> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open(opts.method, opts.url);
      for (const [key, value] of Object.entries(opts.headers)) {
        xhr.setRequestHeader(key, value);
      }

      if (opts.onRawProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) opts.onRawProgress!(e.loaded);
        };
      } else if (opts.trackProgress && onUploadProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable && e.total > 0) {
            onUploadProgress(Math.min(e.loaded / e.total, 1));
          }
        };
      }

      xhr.onload = () =>
        resolve({
          status: xhr.status,
          statusText: xhr.statusText,
          body: xhr.responseText || "",
        });

      xhr.onerror = () => {
        // A status of 0 here means the browser blocked the response from JS.
        // For a cross-origin request that's almost always either a true network
        // failure OR a server error response (e.g. 413 Request Entity Too
        // Large) whose status/body we can't read because the error response
        // lacked CORS headers. Surface the upload size and that hint so the
        // log is actionable rather than an opaque "network error".
        const size = instance.instanceData.dicomP10Compressed?.size;
        const sizeStr = size ? humanFileSize(size) : "unknown size";
        reject(
          new UploadError(
            `litevna upload failed: network error sending ${sizeStr} ` +
              `(the server may have rejected the upload)`,
            { status: xhr.status || undefined }
          )
        );
      };
      xhr.ontimeout = () =>
        reject(new UploadError("litevna upload failed: timed out"));

      xhr.send(opts.body);
    });
  }

  // Direct path: POST the gzipped P10 straight through the worker.
  async function uploadToLitevnaDirect(): Promise<any> {
    const url = joinPaths(
      uploadCredentials.endpoint!,
      `/studies?studyExternalId=${studyShortid}`
    );
    const res = await xhrSend({
      method: "POST",
      url,
      headers: {
        "Content-Type": "application/dicom",
        Authorization: `Bearer ${uploadCredentials.sessionToken}`,
      },
      body: instance.instanceData.dicomP10Compressed ?? null,
      trackProgress: true,
    });
    if (res.status < 200 || res.status >= 300) {
      throw new UploadError(
        `litevna upload failed: ${res.status} ${res.statusText}`,
        { status: res.status, body: res.body.slice(0, 2000) }
      );
    }
    // Bytes are all on the wire; make sure the bar reads full before we hand
    // off (onprogress may stop just short of total).
    onUploadProgress?.(1);
    let uploadResponse: any;
    try {
      uploadResponse = JSON.parse(res.body);
    } catch {
      throw new UploadError("litevna upload returned non-JSON response", {
        status: res.status,
        body: res.body.slice(0, 2000),
      });
    }
    console.log("uploaded instance", instance.uid, "response: ", uploadResponse);
    return uploadResponse;
  }

  // Multipart path: for artifacts too large to POST through the worker.
  //
  // Uses R2's native multipart API, driven entirely through the worker on our
  // own domain (no cloudflarestorage.com — which corporate networks block, and
  // whose cross-origin errors are opaque). Four steps:
  //   1. create     — worker starts a multipart upload, returns ids.
  //   2. uploadpart — PUT-equivalent POST of each ≤cap chunk; worker streams it
  //                   into R2 and returns the part's etag.
  //   3. complete   — worker assembles the parts into the staged object.
  //   4. trigger    — POST /studies?uploadId=… runs the normal STOW pipeline,
  //                   identical to the small-file path's finalize.
  // All four are same-domain through the gateway, so failures surface real
  // HTTP status/body instead of an opaque cross-origin network error.
  async function uploadToLitevnaMultipart(): Promise<any> {
    const root = uploadCredentials.endpoint!;
    const auth = `Bearer ${uploadCredentials.sessionToken}`;
    const blob = instance.instanceData.dicomP10Compressed;
    if (!blob) {
      throw new UploadError("litevna multipart upload: no compressed body");
    }

    // R2 requires every part except the last to be equal-sized and ≥5 MiB, with
    // ≤10,000 parts. 90 MiB stays under Cloudflare's ~100MB request cap and
    // ceilings out around 900 GiB — far beyond any single DICOM artifact.
    const PART_SIZE = 90 * 1024 * 1024;
    const total = blob.size;

    // Step 1: create the multipart upload.
    const createRes = await fetch(
      joinPaths(root, "/studies?multipartAction=create"),
      { method: "POST", headers: { Authorization: auth } }
    );
    if (!createRes.ok) {
      const body = await createRes.text().catch(() => "<unreadable body>");
      throw new UploadError(
        `litevna multipart create failed: ${createRes.status} ${createRes.statusText}`,
        { status: createRes.status, body: body.slice(0, 2000) }
      );
    }
    const { uploadId, r2UploadId } = (await createRes.json()) as {
      uploadId: string;
      r2UploadId: string;
    };
    const r2UploadIdEnc = encodeURIComponent(r2UploadId);

    // Step 2: upload parts sequentially. Sequential keeps memory flat and lets
    // the progress bar advance monotonically; a single failed part is retried by
    // the outer retry() wrapper (the whole instance restarts, which is fine —
    // create is cheap and orphaned multipart uploads auto-abort after 7 days).
    const parts: { partNumber: number; etag: string }[] = [];
    let uploadedBytes = 0;
    for (let offset = 0, partNumber = 1; offset < total; offset += PART_SIZE, partNumber++) {
      const chunk = blob.slice(offset, offset + PART_SIZE);
      const base = uploadedBytes;
      const partRes = await xhrSend({
        method: "POST",
        url: joinPaths(
          root,
          `/studies?multipartAction=uploadpart&uploadId=${uploadId}&r2UploadId=${r2UploadIdEnc}&partNumber=${partNumber}`
        ),
        headers: { Authorization: auth },
        body: chunk,
        onRawProgress: (loaded) =>
          onUploadProgress?.(Math.min((base + loaded) / total, 1)),
      });
      if (partRes.status < 200 || partRes.status >= 300) {
        throw new UploadError(
          `litevna multipart part ${partNumber} failed: ${partRes.status} ${partRes.statusText}`,
          { status: partRes.status, body: partRes.body.slice(0, 2000) }
        );
      }
      let part: { partNumber: number; etag: string };
      try {
        part = JSON.parse(partRes.body);
      } catch {
        throw new UploadError(
          `litevna multipart part ${partNumber} returned non-JSON response`,
          { status: partRes.status, body: partRes.body.slice(0, 2000) }
        );
      }
      parts.push(part);
      uploadedBytes += chunk.size;
    }

    // Step 3: complete — assemble the parts into the staged object.
    const completeRes = await fetch(
      joinPaths(
        root,
        `/studies?multipartAction=complete&uploadId=${uploadId}&r2UploadId=${r2UploadIdEnc}`
      ),
      {
        method: "POST",
        headers: { Authorization: auth, "Content-Type": "application/json" },
        body: JSON.stringify({ parts }),
      }
    );
    if (!completeRes.ok) {
      const body = await completeRes.text().catch(() => "<unreadable body>");
      throw new UploadError(
        `litevna multipart complete failed: ${completeRes.status} ${completeRes.statusText}`,
        { status: completeRes.status, body: body.slice(0, 2000) }
      );
    }
    onUploadProgress?.(1);

    // Step 4: trigger processing. studyExternalId is still required by the
    // archive, so it must ride along on the trigger request too. The worker now
    // ingests the (large) staged file synchronously, which can take a while —
    // the upload bar is already full, so surface a live status message with
    // elapsed seconds so the user knows it's working, not hung/failed.
    const startedAt = Date.now();
    const elapsedSeconds = () => Math.round((Date.now() - startedAt) / 1000);
    const heartbeat = setInterval(() => {
      updateStatus(
        "Upload complete — server is processing the study " +
          `(large images can take a while)… ${elapsedSeconds()}s`
      );
    }, 1000);

    try {
      const triggerRes = await fetch(
        joinPaths(
          root,
          `/studies?uploadId=${uploadId}&studyExternalId=${studyShortid}`
        ),
        { method: "POST", headers: { Authorization: auth } }
      );
      if (!triggerRes.ok) {
        const body = await triggerRes.text().catch(() => "<unreadable body>");
        throw new UploadError(
          `litevna processing trigger failed: ${triggerRes.status} ${triggerRes.statusText}`,
          { status: triggerRes.status, body: body.slice(0, 2000) }
        );
      }
      const uploadResponse = await triggerRes.json();
      console.log(
        "uploaded instance (multipart)",
        instance.uid,
        "response: ",
        uploadResponse
      );
      return uploadResponse;
    } catch (err) {
      // Replace the "processing…" message so it doesn't linger as if still
      // working. The retry wrapper decides whether another attempt follows.
      updateStatus(
        `Server processing failed after ${elapsedSeconds()}s — retrying if attempts remain…`
      );
      throw err;
    } finally {
      clearInterval(heartbeat);
    }
  }

  async function uploadToS3(client: S3Client) {
    const uploadCommand = new PutObjectCommand({
      Bucket: uploadCredentials.bucket,
      Body: instance.instanceData.dicomP10Compressed,
      Key: fileKey,
      ContentEncoding: "gzip",
      ContentType: "application/dicom",
    });
    const uploadResponse = await client.send(uploadCommand);
    console.log("uploaded instance", instance.uid, "response: ", uploadResponse);
    return uploadResponse;
  }

  try {
    return await retry(doUpload, {
      retries: UPLOAD_RETRIES,
      baseDelayMs: 500,
      onRetry: (err, attempt) => {
        const msg = `Upload attempt ${attempt} failed for instance ${
          instance.uid
        }, retrying: ${describeError(err)}`;
        console.warn(msg);
        addLog("warn", msg);
      },
    });
  } catch (err) {
    const uploadErr = err as UploadError;
    Sentry.captureException(err, {
      tags: {
        source: "upload-instance",
        storageProvider: uploadCredentials.storageProvider,
      },
      extra: {
        instanceUid: instance.uid,
        sopInstanceUID: instance.sopInstanceUID,
        fileKey,
        studyShortid,
        attempts: UPLOAD_RETRIES + 1,
        responseStatus: uploadErr.status,
        responseBody: uploadErr.body,
      },
    });
    addLog(
      "error",
      `Upload failed for instance ${instance.uid} after ${
        UPLOAD_RETRIES + 1
      } attempts: ${describeError(err)}`
    );
    throw err;
  }
}
